import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../utils/api";

const initialState = {
  name: "",
  mobile: "",
  email: "",
  address: "",
  walletBalance: 0,
  loyaltyPoints: 0,
};

const CustomerFormModal = ({ customer, onClose, refresh }) => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm({
        name: customer.name || "",
        mobile: customer.mobile || "",
        email: customer.email || "",
        address: customer.address || "",
        walletBalance: customer.walletBalance || 0,
        loyaltyPoints: customer.loyaltyPoints || 0,
      });
    } else {
      setForm(initialState);
    }
  }, [customer]);

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return false;
    }

    if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      toast.error("Invalid mobile number");
      return false;
    }

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Invalid email format");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      if (customer) {
        await api.put(`/customers/${customer._id}`, form);
        toast.success("Customer updated successfully");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added successfully");
      }

      refresh();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error saving customer"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-8 relative animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">
              {customer ? "Edit Customer" : "Add Customer"}
            </h2>
            <p className="text-sm text-gray-500">
              {customer
                ? "Update customer details"
                : "Create a new customer profile"}
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-black text-xl"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-sm font-medium text-gray-600">
              Customer Name
            </label>
            <input
              type="text"
              placeholder="Enter full name"
              className="mt-1 w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={form.name}
              onChange={(e) =>
                setForm({ ...form, name: e.target.value })
              }
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="text-sm font-medium text-gray-600">
              Mobile Number
            </label>
            <input
              type="text"
              placeholder="Enter mobile number"
              className="mt-1 w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100"
              value={form.mobile}
              disabled={customer}
              onChange={(e) =>
                setForm({ ...form, mobile: e.target.value })
              }
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium text-gray-600">
              Email (Optional)
            </label>
            <input
              type="email"
              placeholder="Enter email address"
              className="mt-1 w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
            />
          </div>

          {/* Address */}
          <div>
            <label className="text-sm font-medium text-gray-600">
              Address (Optional)
            </label>
            <textarea
              rows={3}
              placeholder="Enter address"
              className="mt-1 w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={form.address}
              onChange={(e) =>
                setForm({ ...form, address: e.target.value })
              }
            />
          </div>

          {/* Wallet Balance & Loyalty Points */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">
                Wallet Balance (₹)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                className="mt-1 w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={form.walletBalance}
                onChange={(e) =>
                  setForm({ ...form, walletBalance: parseFloat(e.target.value) || 0 })
                }
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">
                Loyalty Points
              </label>
              <input
                type="number"
                min="0"
                placeholder="0"
                className="mt-1 w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={form.loyaltyPoints}
                onChange={(e) =>
                  setForm({ ...form, loyaltyPoints: parseInt(e.target.value) || 0 })
                }
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border rounded-lg hover:bg-gray-100"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading
                ? "Saving..."
                : customer
                ? "Update Customer"
                : "Save Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerFormModal;
